import { Link } from "@tanstack/react-router"
import type { ProductVisualKind } from "@nova-ai/product-visual-templates/kinds"
import {
  PRODUCT_VISUAL_STUDIO_STATUS,
  type ProductVisualStudioStatus,
  type StoreCode,
} from "@nova-ai/shared"
import { Ban, CircleCheck, Grid2x2, TriangleAlert } from "lucide-react"

import { useProductVisualImages } from "../../services/use-product-visual-images"
import { useProductVisualWorklist } from "../../services/use-product-visual-worklist"
import { STUDIO_HISTORY_SOURCE_KINDS } from "../pim-workflow-history/workflow-history-context"
import { ProductStoreSelect } from "../products/product-store-select"
import { ScrollToTop } from "../ui/scroll-to-top"
import { SegmentedControl } from "../ui/segmented-control"
import { ProductVisualImagesGrid } from "./product-visual-images-grid"
import { ProductVisualKindControl, productVisualKindLabel } from "./product-visual-kind-control"
import { ProductVisualWorklist } from "./product-visual-worklist"

export function ProductVisualStudioWorklists({
  store,
  visualKind,
  status,
  onStoreChange,
  onVisualKindChange,
  onStatusChange,
}: {
  store: StoreCode
  visualKind: ProductVisualKind
  status: ProductVisualStudioStatus
  onStoreChange: (store: StoreCode) => void
  onVisualKindChange: (kind: ProductVisualKind) => void
  onStatusChange: (status: ProductVisualStudioStatus) => void
}) {
  const images = useProductVisualImages(
    { store, kind: visualKind },
    status === PRODUCT_VISUAL_STUDIO_STATUS.images
  )
  const worklistStatus =
    status === PRODUCT_VISUAL_STUDIO_STATUS.images ? PRODUCT_VISUAL_STUDIO_STATUS.ready : status
  const worklist = useProductVisualWorklist(
    { store, kind: visualKind, status: worklistStatus },
    status !== PRODUCT_VISUAL_STUDIO_STATUS.images
  )
  const counts = status === PRODUCT_VISUAL_STUDIO_STATUS.images ? images.counts : worklist.counts

  return (
    <>
      <section className="space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-4">
              <h2 className="text-[15px] font-semibold tracking-tight text-foreground">
                {statusLabel(status)}
              </h2>

              <Link
                to="/studio/history"
                search={{ store, kind: STUDIO_HISTORY_SOURCE_KINDS[visualKind], limit: 25 }}
                className="inline-flex min-h-8 items-center rounded-full text-sm text-info hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Workflow history
              </Link>
            </div>

            <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
              {`${store} · ${productVisualKindLabel(visualKind)}. Images are published pointers. The other worklists describe current eligible render work and use a different population.`}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <ProductVisualKindControl value={visualKind} onChange={onVisualKindChange} />

            <ProductStoreSelect value={store} onChange={onStoreChange} />
          </div>
        </header>

        <SegmentedControl
          label="Studio worklist"
          value={status}
          options={[
            {
              value: PRODUCT_VISUAL_STUDIO_STATUS.images,
              label: "Images",
              icon: Grid2x2,
              count: counts?.images ?? images.total,
            },
            {
              value: PRODUCT_VISUAL_STUDIO_STATUS.ready,
              label: "Ready to render",
              icon: CircleCheck,
              ...(counts ? { count: counts.ready } : {}),
            },
            {
              value: PRODUCT_VISUAL_STUDIO_STATUS.gaps,
              label: "Gaps",
              icon: TriangleAlert,
              ...(counts ? { count: counts.gaps } : {}),
            },
            {
              value: PRODUCT_VISUAL_STUDIO_STATUS.excluded,
              label: "Excluded",
              icon: Ban,
              ...(counts ? { count: counts.excluded } : {}),
            },
          ]}
          onChange={onStatusChange}
          className="max-w-full overflow-x-auto"
        />

        {status === PRODUCT_VISUAL_STUDIO_STATUS.images ? (
          <ProductVisualImagesGrid
            store={store}
            visualKind={visualKind}
            images={images.images}
            isLoading={images.isLoading}
            isFetchingNextPage={images.isFetchingNextPage}
            hasNextPage={images.hasNextPage}
            error={images.error}
            onLoadMore={images.fetchNextPage}
          />
        ) : (
          <ProductVisualWorklist
            store={store}
            visualKind={visualKind}
            status={status}
            items={worklist.items}
            isLoading={worklist.isLoading}
            isFetchingNextPage={worklist.isFetchingNextPage}
            hasNextPage={worklist.hasNextPage}
            error={worklist.error}
            onRetry={worklist.refetch}
            onLoadMore={worklist.fetchNextPage}
          />
        )}
      </section>

      <ScrollToTop />
    </>
  )
}

function statusLabel(status: ProductVisualStudioStatus): string {
  if (status === PRODUCT_VISUAL_STUDIO_STATUS.images) {
    return "Generated images"
  }
  if (status === PRODUCT_VISUAL_STUDIO_STATUS.ready) {
    return "Ready to render"
  }
  if (status === PRODUCT_VISUAL_STUDIO_STATUS.gaps) {
    return "Gaps"
  }
  return "Excluded"
}
